import { useQuery } from '@apollo/client';
import { Box, DialogContent, Divider } from '@mui/material';
import { styled } from '@mui/material/styles';
import DocumentIntroTitle from 'FeatureModules/Document/Components/DocumentIntroTitle';
import Loader from 'Components/Forms/Loader';
import ReceivedRequestCard from 'FeatureModules/Home/Components/Requests/ReceivedRequestCard';
import useDialog from 'hooks/useDialog';
import { useLogEvent } from 'hooks/useLogEvent';
import useMessages from 'hooks/useMessages';
import usePartnerRequestActions from 'FeatureModules/Partner/Components/PartnerRequests/usePartnerRequestActions';
import CompanyNameLogo from 'Components/CompanyNameLogo';
import QuestionnaireTitle from 'FeatureModules/Questionnaire/Components/QuestionnaireTable/QuestionnaireTitle';
import { CardContainer, DialogDefault, ThemeButton, ThemeTypography } from 'DesignSystem';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import {
  GET_QUESTIONNAIRE_OVERVIEW,
  GET_QUESTIONNAIRE_COMPANY_RESPONSE,
} from 'graphql/secure/queries/questionnaire.queries';
import React, { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { IDefaultDialogProps } from 'types/dialog.types';
import { EAvailableSizes } from 'DesignSystem/types/size.types';
import { EPartnerRequestStatus, IPartnerRequest } from 'FeatureModules/Partner/types/partner.types';
import {
  IQuestionnaire,
  IQuestionnaireCompanyResponse,
} from 'FeatureModules/Questionnaire/types/questionnaire.types';

interface IReviewQuestionnaireRequestDialogProps extends IDefaultDialogProps {
  request: IPartnerRequest;
}

const ActionBar = styled(Box)(({ theme }) => ({
  width: '100%',
  right: 0,
  backgroundColor: '#fff',
  height: theme.spacing(10),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(0, 3),
  boxShadow: theme.custom.shadows[1],
}));

const StyledDialog = styled(DialogDefault)(({ theme }) => ({
  '& .MuiDialog-paper': {
    minWidth: 550,
  },
  '& h2': {
    padding: '16px 24px 0 !important',
  },
}));

const ReviewQuestionnaireRequestDialog: FC<IReviewQuestionnaireRequestDialogProps> = ({
  open,
  onClose,
  request,
}) => {
  const { openDialog } = useDialog();
  const navigate = useNavigate();
  const { logEvent } = useLogEvent();
  const { updatePartnerRequest } = usePartnerRequestActions();
  const { setErrorMessage, setSuccessMessage } = useMessages();

  const { data, loading } = useQuery<{
    questionnaire: IQuestionnaire;
  }>(GET_QUESTIONNAIRE_OVERVIEW, {
    variables: { id: request.questionnaireId },
    skip: !request.questionnaireId,
  });

  const { data: questionnaireResponseData, loading: questionnaireResponseLoading } = useQuery<{
    questionnaireCompanyResponse: IQuestionnaireCompanyResponse;
  }>(GET_QUESTIONNAIRE_COMPANY_RESPONSE, {
    variables: {
      id: request.questionnaireCompanyResponseId,
    },
    skip: !request.questionnaireCompanyResponseId,
  });

  const questionnaire = data?.questionnaire;
  const questionnaireResponse = questionnaireResponseData?.questionnaireCompanyResponse;

  const handleAccept = () => {
    updatePartnerRequest({
      variables: {
        id: request.id,
        input: {
          requestStatus: EPartnerRequestStatus.PROVIDED_COMPLETED,
        },
      },
      onError: () =>
        setErrorMessage('Something went wrong finishing the request. Please try again later.'),
      onCompleted: () => {
        logEvent('PARTNER_REQUEST_PROVIDED_COMPLETED', {
          requestType: request.requestType,
        });
        setSuccessMessage('You have finished the request');
        onClose?.();
      },
      refetchQueries: ['GetProductsHome', 'GetQuestionnaire', 'GetQuestionnaireCompanyResponses'],
    });
  };

  const handleEditRequest = () => {
    openDialog({
      type: 'PARTNER_REQUEST',
      props: {
        onClose,
        initialRequest: request,
        requestedToCompany: request.requestedToCompany,
      },
    });
  };

  const handleNavigateToQuestionnaire = () => {
    onClose?.();
    navigate(
      `/questionnaires/${request?.questionnaireId}/responses/${request?.questionnaireCompanyResponseId}`
    );
  };

  return (
    <StyledDialog
      title="Review questionnaire response"
      iconName="document-request-manage"
      open={open}
      onClose={onClose}
    >
      <DialogContent>
        <Box mt={2} />
        <ReceivedRequestCard displayYourMessage request={request} displayActions={false} />
        {loading || questionnaireResponseLoading || !questionnaire || !questionnaireResponse ? (
          <Loader />
        ) : (
          <CardContainer padding={1} mt={3}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <QuestionnaireTitle
                title={questionnaire.title}
                year={questionnaire.year}
                externalId={questionnaire.externalId}
                riskLevel={questionnaireResponse.riskLevel}
              />
              <ThemeButton
                color="BLUE_ICE"
                onClick={handleNavigateToQuestionnaire}
                startIcon={<Icon name="document-request-manage" size="small" />}
              >
                Review questionnaire
              </ThemeButton>
            </Box>
          </CardContainer>
        )}

        <Box mt={4} mb={3}>
          <Divider />
        </Box>
        <Box display="flex" alignItems="center" mb={1.5}>
          <CompanyNameLogo
            size={EAvailableSizes.MEDIUM}
            companyName={request.requestedToCompany.name}
          />
          <ThemeTypography variant="BODY_LARGE_BOLD">
            &nbsp;has finished the self assessment questionnaire
          </ThemeTypography>
        </Box>
        {request.document && (
          <Box mb={2}>
            <CardContainer padding={1.5} selected>
              <DocumentIntroTitle document={request.document} showOpenInNewTab />
            </CardContainer>
          </Box>
        )}
      </DialogContent>
      <ActionBar>
        <ThemeButton color="BLUE_ICE" type="button" onClick={onClose}>
          Cancel
        </ThemeButton>
        <Box display="flex" justifyContent="end">
          <ThemeButton
            color="BLUE_ICE"
            type="button"
            onClick={handleEditRequest}
            startIcon={<Icon name="edit" />}
            data-testid="edit-request-btn"
          >
            Edit request
          </ThemeButton>
          <Box mr={1} />
          <ThemeButton
            color="PRIMARY"
            type="submit"
            startIcon={<Icon name="document-request-fulfilled" color="WHITE" />}
            onClick={handleAccept}
          >
            Finish request
          </ThemeButton>
        </Box>
      </ActionBar>
    </StyledDialog>
  );
};

export default ReviewQuestionnaireRequestDialog;
